<template>
    <div>
        <div v-if="!isDocTypeField" class="form-group">
            <label class="mb-1">Type</label>
            <multiselect
                :value="fieldEditorsInfo.find(el => el.type === editor.type)"
                :options="fieldEditorsInfo"
                @select="onTypeChange"
                placeholder="Select a section"
                track-by="type"
                label="label">
            </multiselect>
        </div>

        <div v-if="!isLineField && !isDocTypeField" class="form-group">
            <label for="placeholder">
                Placeholder
            </label>
            <input type="text"
                   :value="editor.placeholder"
                   @change="e => updateEditorProp('placeholder', e.target.value)"
                   id="placeholder"
                   class="form-control form-control-sm">
        </div>

        <template v-if="FieldEditorTypes.NUMERIC === editor.type">
            <div class="form-group">
                <label>Rounding decimals</label>
                <vue-number-input
                    :value="numeric.precision || defaultRounding"
                    @change="v => updateEditorProp(['numeric', 'precision'], v)"
                    :min="0"
                    :max="10"
                    controls
                    size="small">
                </vue-number-input>
            </div>
            <div class="form-group">
                <label>Thousand separator</label>
                <AppRadioButtonsGroup
                    label="label"
                    trackBy="value"
                    :value="numeric.thousandSeparator || null"
                    :options="numericSeparators"
                    @update="v => updateEditorProp(['numeric', 'thousandSeparator'], v)"/>
            </div>
        </template>

        <template v-if="!isLineField && FieldEditorTypes.TEXT === editor.type">
            <div class="form-group">
                <label for="mask">
                    Mask
                    <span v-tooltip="maskInputTooltip">
                        <i class="fal fa-info-circle"></i>
                    </span>
                </label>
                <input type="text"
                       :value="editor.mask"
                       @change="e => updateEditorProp('mask', e.target.value)"
                       id="mask"
                       class="form-control form-control-sm">
            </div>
        </template>
        <template v-if="[FieldEditorTypes.DD_SINGLE, FieldEditorTypes.DD_MULTIPLE].includes(editor.type)">
            <div v-if="!isDocTypeField" class="form-group">
                <label>Options source</label>
                <AppRadioButtonsGroup
                    label="label"
                    trackBy="source"
                    :key="selectSourceInputKey"
                    :value="select.source"
                    :options="selectOptionsSources"
                    @update="onSelectOptionsSourceChange"/>
            </div>

            <div v-if="FieldEditorOptionsSources.STORAGE === select.source">
                <div class="form-group">
                    <multiselect
                        :value="getFieldStorageOptionsInfo().find(el => select.storage && el.name === select.storage.getter)"
                        :options="getFieldStorageOptionsInfo()"
                        @select="onStorageGetterChange"
                        track-by="name"
                        label="label">
                    </multiselect>
                </div>
            </div>
            <div v-if="FieldEditorOptionsSources.DATASET === select.source">
                <div class="form-group">
                    <multiselect
                        :value="datasetsList.find(el => select.dataset && el.uuid === select.dataset.uuid)"
                        :options="datasetsList"
                        @select="onStorageDatasetChange"
                        :loading="isDatasetsListLoading"
                        track-by="uuid"
                        label="name">
                    </multiselect>
                </div>
                <a @click.prevent="manageDatasets" href="#" class="text-primary-middle font-12 d-block my-2">
                    <i class="fa fa-table"></i>
                    Manage datasets
                </a>
            </div>
            <FieldOptionsApiConfig
                v-else-if="FieldEditorOptionsSources.API === select.source && !isDocTypeField"
                :api="select.api"
                @update="updateApiConfig">
                <template slot="footer">
                    <div v-if="isLineField" class="custom-control custom-switch my-2">
                        <input class="custom-control-input" type="checkbox"
                               :value="select.api.attachLineData"
                               :checked="select.api.attachLineData"
                               @change="onApiParamsLinesDataAttachmentToggle"
                               id="attachLineData">
                        <label class="custom-control-label text-dark pt-1" for="attachLineData">
                            Attach line data
                            <span v-tooltip="`Attach edited line data to the api request params as 'line' property`">
                                <i class="fal fa-info-circle"></i>
                            </span>
                        </label>
                    </div>
                    <div class="custom-control custom-switch my-2">
                        <input class="custom-control-input" type="checkbox"
                               :value="select.api.paginated"
                               :checked="select.api.paginated"
                               @change="onApiParamsPaginationToggle"
                               id="paginated">
                        <label class="custom-control-label text-dark pt-1" for="paginated">
                            Paginated
                            <span
                                v-tooltip="`If the request is paginated, it will be reloaded depending on the query string`">
                                <i class="fal fa-info-circle"></i>
                            </span>
                        </label>
                    </div>
                    <div v-if="select.api.paginated" class="mb-1">
                        <span
                            class="text-muted"
                            v-tooltip="`Parameter name that will be used to send the value as a search string to the server`">
                            <i class="fal fa-info-circle"></i>
                        </span>
                        Query parameter name
                        <AppEditableText
                            :value="select.api.queryStringParamName || FIELD_API_OPTIONS_DEFAULT_QUERY_PARAM_NAME"
                            @update="onApiQueryParamNameChange"
                            class="d-inline font-weight-bold">
                            <div slot="popover-header" class="text-muted text-left">
                                Query parameter name
                            </div>
                            <template slot="popover-footer" slot-scope="{value}">
                                <div v-if="!value" class="text-danger text-left font-12">
                                    Value is required
                                </div>
                            </template>
                        </AppEditableText>
                    </div>
                </template>
            </FieldOptionsApiConfig>

            <AppSwitch v-if="editor.type === FieldEditorTypes.DD_SINGLE && !isLineField" :value="select.isTreeselect"
                       @toggle="onApiParamsTreeselectToggle"
                       class="my-2">
                <div slot="label">
                    Is treeselect
                    <span v-tooltip="'Treeselect will group values by the `parent.uuid` property'">
                        <i class="fal fa-info-circle"></i>
                    </span>
                </div>
            </AppSwitch>

            <div>
                <span class="text-muted" v-tooltip="'Unique key to store the value'">
                    <i class="fal fa-info-circle"></i>
                </span>
                Option track by:
                <AppEditableText
                    :value="select.trackBy"
                    :disabled="FieldEditorOptionsSources.STORAGE === select.source || isDocTypeField"
                    @update="onSelectTrackByUpdate"
                    class="d-inline font-weight-bold">
                    <div slot="popover-header" class="text-muted text-left">
                        Track by
                    </div>
                    <template slot="popover-footer" slot-scope="{value}">
                        <div v-if="!value" class="text-danger text-left font-12">
                            Value is required
                        </div>
                    </template>
                </AppEditableText>
            </div>
            <div class="mt-1">
                <span class="text-muted" v-tooltip="'Unique key to display in the interface'">
                    <i class="fal fa-info-circle"></i>
                </span>
                Option label:
                <AppEditableText
                    :value="select.label"
                    :disabled="isDocTypeField"
                    @update="onSelectLabelUpdate"
                    class="d-inline font-weight-bold">
                    <div slot="popover-header" class="text-muted text-left">
                        Label
                    </div>
                    <template slot="popover-footer" slot-scope="{value}">
                        <div v-if="!value" class="text-danger text-left font-12">
                            Value is required
                        </div>
                        <div v-if="select.source !== FieldEditorOptionsSources.STATIC" class="text-muted font-12 mt-2">
                            <i class="fal fa-info-circle"></i>
                            Use <b class="text-purple">{ }</b> to create a complex label, e.g.:
                            <div class="border-left pl-3 ml-3 mt-1">
                                <div class="text-bold">
                                    (<b class="text-purple">{rate}</b> %) <b class="text-purple">{name}</b>
                                </div>
                                <div>will be converted into</div>
                                <div class="text-bold">(18.00 %) Purchases Tax</div>
                            </div>
                        </div>
                    </template>
                </AppEditableText>
            </div>
            <div class="mt-2 font-italic">
                Options:
            </div>
            <FieldOptionsTable
                :key="field.path + select.source + select.getter"
                :field="field"
                @update="onSelectOptionsUpdate"
                class="mt-2"/>
            <FieldOptionsFilter
                :field="field"
                @update="onSelectOptionsFiltersUpdate"
                class="mt-2"/>
        </template>

        <div v-if="isTextFieldWithDropdownAttached" class="mt-4">
            <div v-if="isLinesSkuField" class="custom-control custom-switch mb-3">
                <input class="custom-control-input" type="checkbox"
                       :value="select.showDescription"
                       :checked="select.showDescription"
                       @change="onSkuShowDescriptionParamToggle"
                       id="showDescription">
                <label class="custom-control-label text-dark pt-1" for="showDescription">
                    Show description
                    <span v-tooltip="`Show stock item description below item code`">
                        <i class="fal fa-info-circle"></i>
                    </span>
                </label>
            </div>

            <span v-tooltip="'There is an additional dropdown attached to this text field'">
                <i class="fal fa-info-circle"></i>
                Options:
            </span>
            <FieldOptionsFilter
                :field="field"
                @update="onSelectOptionsFiltersUpdate"
                class="mt-2"/>
        </div>

        <div v-if="!isDocTypeField" class="form-group mt-3">
            <label>
                Default value
            </label>
            <DocumentViewerFieldEditor
                :field="field"
                :value="editor.default"
                @change="data => updateEditorProp('default', data)"/>
            <AppSwitch v-model="editor.forceOverrideWithDefault" class="mt-1">
                <div slot="label">
                    Force overwrite
                    <span class="text-danger pl-1"
                          v-tooltip="`
                                Attention, activating this flag will cause the field value to be overwritten with the default value each time the form is loaded. <br/>
                                <i class='font-12'>You probably only need this option in very extreme cases for read-only fields.</i>`
                          ">
                        <i class="fal fa-circle-exclamation"></i>
                    </span>
                </div>
            </AppSwitch>
        </div>
    </div>
</template>

<script>
import {fieldEditorsInfo, FieldEditorTypes} from '@/types/document-viewer/field/modules/editor'
import AppEditableText from '../../../../../../../../components/shared/AppEditableText'
import AppRadioButtonsGroup from '../../../../../../../../components/shared/AppRadioButtonsGroup'
import FieldOptionsTable from './FieldOptionsTable'
import axios from 'axios'
import {withConfirmation} from '@/decorators/withConfirmation'
import {SETTINGS_DATASETS} from '@/views/settings/settings-tabs'
import FieldOptionsFilter from './FieldOptionsFilter'
import {FormSystemSections} from '@/types/document-viewer/section'
import {mapGetters} from 'vuex'
import FieldOptionsApiConfig from './FieldOptionsApiConfig'
import DocumentViewerFieldEditor from '../../../../../../single/view/DocumentViewerFieldEditor'
import {
    FIELD_API_OPTIONS_DEFAULT_QUERY_PARAM_NAME,
    FieldEditorOptionsSources,
    FieldStorageOptionsGetters,
    getFieldStorageOptionsInfo,
    getFieldStorageOptionsLabel,
    getFieldStorageOptionsTrackBy
} from '@/types/document-viewer/field/modules/editor/select'
import {FieldEditorNumericSeparators} from '@/types/document-viewer/field/modules/editor/numeric'
import {getRandomId} from '@/utils'
import AppSwitch from '@/components/shared/AppSwitch'

export default {
    name: 'FieldInputEditor',
    components: {
        AppSwitch,
        DocumentViewerFieldEditor,
        FieldOptionsApiConfig,
        FieldOptionsFilter,
        FieldOptionsTable,
        AppRadioButtonsGroup,
        AppEditableText
    },
    props: {
        field: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            datasetsList: [],
            isDatasetsListLoading: false,

            selectOptionsSources: [{
                source: FieldEditorOptionsSources.STATIC,
                label: 'Create',
                tooltip: 'Create options'
            }, {
                source: FieldEditorOptionsSources.STORAGE,
                label: 'Preset',
                tooltip: 'Select frequently used preset data'
            }, {
                source: FieldEditorOptionsSources.DATASET,
                label: 'Dataset',
                tooltip: 'Take data from datasets'
            }, {
                source: FieldEditorOptionsSources.API,
                label: 'API',
                tooltip: 'Get data from api request'
            }],

            numericSeparators: [{
                label: 'None',
                value: null
            }, {
                label: 'Comma',
                value: FieldEditorNumericSeparators.COMMA
            }, {
                label: 'Dot',
                value: FieldEditorNumericSeparators.DOT
            }, {
                label: 'Space',
                value: FieldEditorNumericSeparators.SPACE
            }],

            selectSourceInputKey: getRandomId(),

            FieldEditorTypes,
            FieldEditorOptionsSources,
            getFieldStorageOptionsInfo,
            fieldEditorsInfo,
            FIELD_API_OPTIONS_DEFAULT_QUERY_PARAM_NAME
        }
    },
    computed: {
        ...mapGetters('doc_viewer_settings', [
            'defaultRounding'
        ]),

        ...mapGetters('documents', [
            'isSaleDocs'
        ]),

        editor() {
            return this.field.editor || {}
        },

        select() {
            return this.editor.select || {}
        },

        section() {
            return this.field.section || {}
        },

        numeric() {
            return this.editor.numeric || {}
        },

        isLineField() {
            return this.section.key === FormSystemSections.LINES
        },

        isAllocationField() {
            return this.section.key === FormSystemSections.ALLOCATIONS
        },

        isCustom() {
            return this.field.custom
        },

        isLinesSkuField() {
            return this.field.path === 'lines.sku'
        },

        isCompanyNameField() {
            return this.field.path === 'company.name'
        },

        isDocTypeField() {
            return this.field.path === 'type'
        },

        isTextFieldWithDropdownAttached() {
            return this.isLinesSkuField || this.isCompanyNameField
        },

        maskInputTooltip() {
            return {
                html: true,
                content: `
                    <div class="">Format input with a specific mask</div>
                    <div class="text-info">Special characters:</div>
                    <ul>
                        <li><span class="text-bold">#</span> - number</li>
                        <li><span class="text-bold">X</span> - alphanumeric</li>
                        <li><span class="text-bold">S</span> - letter</li>
                        <li><span class="text-bold">A</span> - letter, forced to upper case when entered</li>
                        <li><span class="text-bold">a</span> - letter, forced to lower case when entered</li>
                    </ul>
                    <small>Examples</small>
                    <div class="border p-2">
                        <div class="row">
                            <div class="col-4"></div>
                            <div class="col-4">Value</div>
                            <div class="col-4">Mask</div>
                        </div>
                        <div class="row">
                            <div class="col-4">Bank BSB</div>
                            <div class="col-4">012-951</div>
                            <div class="col-4">###-###</div>
                        </div>
                        <div class="row">
                            <div class="col-4">Bank Code</div>
                            <div class="col-4">ANZ</div>
                            <div class="col-4">AAA</div>
                        </div>
                    </div>
                    <small>
                        Leave the field blank to allow any input
                    </small>
                `
            }
        }
    },
    methods: {
        updateEditor(editor) {
            this.$emit('update', editor)
        },

        updateEditorProp(path, value) {
            this.updateEditor(_.set(_.cloneDeep(this.field.editor), path, value))
        },

        onTypeChange({type}) {
            const editor = {type, placeholder: ''}
            if ([FieldEditorTypes.DD_SINGLE, FieldEditorTypes.DD_MULTIPLE].includes(type)) {
                editor.select = {
                    source: FieldEditorOptionsSources.STATIC,
                    trackBy: 'name',
                    label: 'label',
                    options: [] // {name: '', label: ''}
                }
            } else if (FieldEditorTypes.TEXT === type) {
                editor.multiline = false
            }
            this.updateEditor(editor)
        },

        onSelectTrackByUpdate(trackBy) {
            const editor = _.cloneDeep(this.editor)
            if (editor.select.source === FieldEditorOptionsSources.STATIC && _.isArray(editor.select.options)) {
                const prevTrackBy = this.select.trackBy
                editor.select.options = editor.select.options.map(el => ({...el, [trackBy]: el[prevTrackBy]}))
            }
            editor.select.trackBy = trackBy
            this.updateEditor(editor)
        },

        onSelectLabelUpdate(label) {
            const editor = _.cloneDeep(this.editor)
            if (editor.select.source === FieldEditorOptionsSources.STATIC && _.isArray(editor.select.options)) {
                const prevLabel = this.select.label
                editor.select.options = editor.select.options.map(el => ({...el, [label]: el[prevLabel]}))
            }
            editor.select.label = label
            this.updateEditor(editor)
        },

        onSelectOptionsSourceChange(source) {
            const editor = _.cloneDeep(this.editor)
            const updateEditor = () => {
                if (source === FieldEditorOptionsSources.STATIC) {
                    editor.select = {
                        options: []
                    }
                } else if (source === FieldEditorOptionsSources.STORAGE) {
                    editor.select = {
                        storage: {
                            getter: null
                        }
                    }
                } else if (source === FieldEditorOptionsSources.DATASET) {
                    editor.select = {
                        dataset: {
                            uuid: null
                        }
                    }
                } else if (source === FieldEditorOptionsSources.API) {
                    editor.select = {
                        api: {
                            url: null,
                            method: 'GET'
                        }
                    }
                }
                editor.select.trackBy = 'name'
                editor.select.label = 'label'
                editor.select.source = source
                this.updateEditor(editor)
            }

            if ((editor.select.options && editor.select.options.length !== 0) ||
                (editor.select.storage && editor.select.storage.getter) ||
                (editor.select.dataset && editor.select.dataset.uuid) ||
                (editor.select.api && editor.select.api.url)) {
                withConfirmation({
                    onConfirm: updateEditor,
                    onCancel: () => {
                        this.selectSourceInputKey = getRandomId()
                    },
                    title: 'Update select source?',
                    description: 'All current settings will be lost'
                })
            } else {
                updateEditor()
            }
        },

        onSelectOptionsFiltersUpdate(filters) {
            const editor = _.cloneDeep(this.editor)
            _.set(editor, 'select.filters', filters)
            this.updateEditor(editor)
        },

        onSelectOptionsUpdate(options) {
            const editor = _.cloneDeep(this.editor)
            _.set(editor, 'select.options', options)
            this.updateEditor(editor)
        },

        onSkuShowDescriptionParamToggle() {
            const editor = _.cloneDeep(this.editor)
            editor.select.showDescription = !editor.select.showDescription
            this.updateEditor(editor)
        },

        updateApiConfig({path, data}) {
            const editor = _.cloneDeep(this.editor)
            _.set(editor.select.api, path, data)
            this.updateEditor(editor)
        },

        onApiParamsLinesDataAttachmentToggle() {
            const editor = _.cloneDeep(this.editor)
            editor.select.api.attachLineData = !editor.select.api.attachLineData
            this.updateEditor(editor)
        },

        onApiParamsPaginationToggle() {
            const editor = _.cloneDeep(this.editor)
            editor.select.api.paginated = !editor.select.api.paginated
            if (editor.select.api.paginated) {
                editor.select.api.queryStringParamName = FIELD_API_OPTIONS_DEFAULT_QUERY_PARAM_NAME
            }
            this.updateEditor(editor)
        },

        onApiParamsTreeselectToggle() {
            const editor = _.cloneDeep(this.editor)
            editor.select.isTreeselect = !editor.select.isTreeselect
            this.updateEditor(editor)
        },

        onApiQueryParamNameChange(name) {
            const editor = _.cloneDeep(this.editor)
            editor.select.api.queryStringParamName = name || FIELD_API_OPTIONS_DEFAULT_QUERY_PARAM_NAME
            this.updateEditor(editor)
        },

        onStorageGetterChange(getter) {
            const editor = _.cloneDeep(this.editor)
            editor.select = {
                source: FieldEditorOptionsSources.STORAGE,
                trackBy: getFieldStorageOptionsTrackBy(getter.name),
                label: getFieldStorageOptionsLabel(getter.name),
                storage: {
                    getter: getter.name
                }
            }
            this.updateEditor(editor)
        },

        onStorageDatasetChange(dataset) {
            const editor = _.cloneDeep(this.editor)
            if (dataset.settings && _.isArray(dataset.settings.columnDefs)) {
                const trackByColDef = dataset.settings.columnDefs.find(col => col.isDefaultTrackBy)
                if (trackByColDef && trackByColDef.field) {
                    editor.select.trackBy = trackByColDef.field
                }
                const labelColDef = dataset.settings.columnDefs.find(col => col.isDefaultLabel)
                if (labelColDef && labelColDef.field) {
                    editor.select.label = labelColDef.field
                }
            }
            editor.select.dataset = {
                uuid: dataset.uuid
            }

            this.updateEditor(editor)
        },

        fetchDatasetsList() {
            this.isDatasetsListLoading = true
            axios.get('datasets')
                .then(res => {
                    this.datasetsList = res.data.data || []
                })
                .finally(() => {
                    this.isDatasetsListLoading = false
                })
        },

        manageDatasets() {
            const routeData = this.$router.resolve({name: 'settings', params: {page: SETTINGS_DATASETS}})
            window.open(routeData.href, '_blank')
        }
    },
    created() {
        // By default "lines.sku" and "company.name" are text fields, but with attached dropdown.
        // We try to expand these text field settings by adding a filter as if it were a multiselect field type.
        if (this.isTextFieldWithDropdownAttached) {
            if (this.isLinesSkuField) {
                if (_.get(this.select, 'storage.getter') !== FieldStorageOptionsGetters.ITEMS) {
                    this.onStorageGetterChange({name: FieldStorageOptionsGetters.ITEMS})
                }
            }
            if (this.isCompanyNameField) {
                const getter = this.isSaleDocs ? FieldStorageOptionsGetters.CUSTOMERS : FieldStorageOptionsGetters.SUPPLIERS
                if (_.get(this.select, 'storage.getter') !== getter) {
                    this.onStorageGetterChange({name: getter})
                }
            }
        }
    },
    watch: {
        'select.source': {
            handler(source) {
                if (source === FieldEditorOptionsSources.DATASET) {
                    this.fetchDatasetsList()
                }
            },
            immediate: true
        }
    }
}
</script>

<style lang="scss" scoped>
@import "src/views/documents/subtypes/settings/editor/styles";
</style>
